import type { Id } from "../../convex/_generated/dataModel";
import {
  ACTIVITY_OPTIONS,
  EXERCISE_TYPE_OPTIONS,
  GOAL_OPTIONS,
  PACE_OPTIONS,
  type OptionItem,
} from "../onboarding/types";
import { formatNumber } from "../lib/format";

/** Seven days in milliseconds — weekly check-in cadence. */
export const MS_PER_WEEK = 7 * 24 * 60 * 60 * 1000;

/** Minimal profile fields the Plan screen reads (structural subset of the users doc). */
export interface PlanProfile {
  readonly goalType?: string;
  readonly weightKg?: number;
  readonly startingWeightKg?: number;
  readonly targetWeightKg?: number;
  readonly weightLossPace?: string;
  readonly activityLevel?: string;
  readonly avgDailySteps?: number;
  readonly exerciseType?: string;
  readonly planStartedAt?: number;
  readonly dailyGoalKcal?: number;
  readonly dailyProteinG?: number;
  readonly dailyCarbsG?: number;
  readonly dailyFatG?: number;
  readonly dailyFiberG?: number;
  readonly unitWeight?: string;
}

/** Pending/resolved weekly check-in as consumed by the Plan UI. */
export interface CheckinView {
  readonly id: string;
  readonly createdAt: number;
  readonly weekStartDate?: number;
  readonly previousTargetKcal: number;
  readonly newTargetKcal: number;
  readonly adjustmentKcal: number;
  readonly adjusted: boolean;
  readonly paused: boolean;
  readonly explanation: string;
  readonly reasonsJson: string;
  readonly status?: string;
}

/** calculationLog row as consumed by the Plan UI (never rendered as raw JSON). */
export interface CalculationLogEntry {
  readonly kind: string;
  readonly engineVersion: string;
  readonly algorithmVersion: string;
  readonly confidence: string;
  readonly explanation: string;
  readonly createdAt: number;
}

export type CheckinRespondResult =
  | { readonly type: "accepted" }
  | { readonly type: "kept" }
  | { readonly type: "already-resolved" }
  | { readonly type: "failed"; readonly message: string };

/** ConvexError messages embed the structured payload, including the error code. */
export function isAlreadyResolvedError(error: unknown): boolean {
  return error instanceof Error && error.message.includes("ALREADY_RESOLVED");
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : "Something went wrong. Please try again.";
}

/**
 * Drives api.engine.respondToWeeklyCheckin and classifies the outcome so the
 * UI can swap the card into an accepted/kept state or surface a stable notice.
 */
export async function respondToCheckin(
  respond: (accept: boolean) => Promise<unknown>,
  accept: boolean,
): Promise<CheckinRespondResult> {
  try {
    await respond(accept);
    console.log("[Frontend][Plan] weekly check-in response saved", { accept });
    return { type: accept ? "accepted" : "kept" };
  } catch (error) {
    if (isAlreadyResolvedError(error)) {
      console.warn("[Frontend][Plan] weekly check-in already resolved");
      return { type: "already-resolved" };
    }
    console.warn("[Frontend][Plan] respondToWeeklyCheckin failed", error);
    return { type: "failed", message: errorMessage(error) };
  }
}

/** Exact spec copy for a pending weekly check-in recommendation. */
export function checkinRecommendationCopy(previousTargetKcal: number, newTargetKcal: number): string {
  return `We recommend changing your daily target from ${formatNumber(previousTargetKcal)} to ${formatNumber(newTargetKcal)} calories.`;
}

/** reasonsJson is JSON.stringify(string[]); parse defensively, never render raw. */
export function parseCheckinReasons(reasonsJson: string): string[] {
  try {
    const parsed: unknown = JSON.parse(reasonsJson);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((reason): reason is string => typeof reason === "string" && reason.trim().length > 0);
  } catch {
    return [];
  }
}

/** Next check-in date: last check-in + 7 days, else plan start + 7 days, else null. */
export function nextCheckinDate(
  lastCheckinCreatedAt: number | null,
  planStartedAt: number | undefined,
): number | null {
  if (lastCheckinCreatedAt !== null) return lastCheckinCreatedAt + MS_PER_WEEK;
  if (planStartedAt !== undefined) return planStartedAt + MS_PER_WEEK;
  return null;
}

/** Plain-language summary of the most recent resolved check-in row. */
export function lastCheckinSummary(row: CheckinView): string {
  if (row.paused) return "Your check-in was paused — your target stayed the same.";
  if (!row.adjusted) return `No change needed — your daily target stayed at ${formatNumber(row.newTargetKcal)} calories.`;
  const direction = row.newTargetKcal > row.previousTargetKcal ? "increased" : "decreased";
  const statusNote = row.status === "accepted" ? " (accepted)" : row.status === "kept" ? " (you kept your previous target)" : "";
  return `Your daily target ${direction} from ${formatNumber(row.previousTargetKcal)} to ${formatNumber(row.newTargetKcal)} calories${statusNote}.`;
}

/** Latest calorie-target calculation entry (history arrives newest-first). */
export function latestCalorieTargetEntry(
  history: readonly CalculationLogEntry[],
): CalculationLogEntry | null {
  return history.find((entry) => entry.kind === "calorieTarget") ?? null;
}

export function latestEntriesPerKind(
  history: readonly CalculationLogEntry[],
): CalculationLogEntry[] {
  const seen = new Set<string>();
  const result: CalculationLogEntry[] = [];
  for (const entry of history) {
    if (seen.has(entry.kind)) continue;
    seen.add(entry.kind);
    result.push(entry);
  }
  return result;
}

function optionTitle(options: readonly OptionItem[], value: string | undefined): string {
  if (value === undefined) return "—";
  return options.find((option) => option.value === value)?.title ?? "—";
}

export function goalTypeLabel(value: string | undefined): string {
  return optionTitle(GOAL_OPTIONS, value);
}

export function paceLabel(value: string | undefined): string {
  return optionTitle(PACE_OPTIONS, value);
}

export function activityLevelLabel(value: string | undefined): string {
  return optionTitle(ACTIVITY_OPTIONS, value);
}

export function exerciseTypeLabel(value: string | undefined): string {
  return optionTitle(EXERCISE_TYPE_OPTIONS, value);
}

/** Goal weight is only meaningful for weight-change goals (mirrors onboarding). */
export function showsTargetWeight(goalType: string | undefined): boolean {
  return goalType === "lose" || goalType === "gain" || goalType === "muscle";
}

/** Pace only applies to weight-change goals (mirrors onboarding). */
export function showsPace(goalType: string | undefined): boolean {
  return showsTargetWeight(goalType);
}

/** Wires the string-typed card callback to the typed respondToWeeklyCheckin mutation. */
export function createCheckinResponder(
  respond: (args: { checkinId: Id<"weeklyCheckins">; accept: boolean }) => Promise<unknown>,
): (checkinId: string, accept: boolean) => Promise<unknown> {
  return (checkinId, accept) => respond({ checkinId: checkinId as Id<"weeklyCheckins">, accept });
}

function finiteOrNull(value: number | undefined | null): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

/** One macro segment of the daily calorie target (protein/carbs 4 kcal/g, fat 9 kcal/g). */
export interface MacroSegment {
  readonly key: "protein" | "carbs" | "fat";
  readonly label: string;
  readonly grams: number;
  readonly kcal: number;
  /** 0–1 share of the total macro-derived calories. */
  readonly fraction: number;
}

const MACRO_KCAL_PER_G = { protein: 4, carbs: 4, fat: 9 } as const;
const MACRO_LABELS = { protein: "Protein", carbs: "Carbs", fat: "Fat" } as const;

/**
 * Calorie composition of the macro targets as proportional segments for a
 * stacked strip. Omits missing/zero macros and renormalises over the rest.
 * Returns null when nothing is known — never renders a 0-length guess.
 */
export function macroComposition(input: {
  readonly proteinG: number | undefined;
  readonly carbsG: number | undefined;
  readonly fatG: number | undefined;
}): MacroSegment[] | null {
  const entries = (Object.keys(MACRO_KCAL_PER_G) as readonly (keyof typeof MACRO_KCAL_PER_G)[])
    .map((key) => {
      const grams = key === "protein" ? input.proteinG : key === "carbs" ? input.carbsG : input.fatG;
      return { key, grams: finiteOrNull(grams) };
    })
    .filter((entry): entry is { key: keyof typeof MACRO_KCAL_PER_G; grams: number } =>
      entry.grams !== null && entry.grams > 0,
    );
  const totalKcal = entries.reduce((sum, entry) => sum + entry.grams * MACRO_KCAL_PER_G[entry.key], 0);
  if (totalKcal <= 0) return null;
  return entries.map((entry) => {
    const kcal = entry.grams * MACRO_KCAL_PER_G[entry.key];
    return {
      key: entry.key,
      label: MACRO_LABELS[entry.key],
      grams: entry.grams,
      kcal,
      fraction: kcal / totalKcal,
    };
  });
}

/** Two-bar energy-budget scale: both bars share max(expenditure, target) as 100%. */
export interface EnergyBudgetScale {
  readonly expenditureKcal: number;
  readonly targetKcal: number;
  /** 0–1 bar lengths relative to the larger of the two values. */
  readonly expenditureFraction: number;
  readonly targetFraction: number;
  /** Absolute |expenditure − target|, rounded to whole kcal. */
  readonly differenceKcal: number;
  /** Deficit: eating below burn. Surplus: eating above burn. */
  readonly kind: "deficit" | "surplus" | "maintenance";
}

/**
 * Local scale for the energy-budget bars plus the deterministic difference.
 * Returns null when either side is unknown — the card then shows
 * "Not enough data yet" instead of bars.
 */
export function energyBudgetScale(
  expenditureKcal: number | null,
  targetKcal: number | null,
): EnergyBudgetScale | null {
  if (expenditureKcal === null || targetKcal === null) return null;
  if (!Number.isFinite(expenditureKcal) || !Number.isFinite(targetKcal)) return null;
  const max = Math.max(expenditureKcal, targetKcal);
  if (max <= 0) return null;
  const diff = Math.round(expenditureKcal - targetKcal);
  return {
    expenditureKcal,
    targetKcal,
    expenditureFraction: expenditureKcal / max,
    targetFraction: targetKcal / max,
    differenceKcal: Math.abs(diff),
    kind: diff > 0 ? "deficit" : diff < 0 ? "surplus" : "maintenance",
  };
}

/** Horizontal layout fractions for the forecast rail (Today → check-in → goal band). */
export interface ForecastTimelineLayout {
  /** 0–1 position of the next check-in marker; null when unknown or outside the span. */
  readonly checkinFraction: number | null;
  /** 0–1 left edge of the goal-date band (goalDateMin). */
  readonly bandStartFraction: number;
  /** 0–1 band width (goalDateMin → goalDateMax), never below 2% so it stays visible. */
  readonly bandWidthFraction: number;
}

/**
 * Maps epoch-ms dates onto the forecast rail: Today sits at 0%, the goal
 * window is a shaded band from min to max (never a falsely exact point), and
 * the next check-in is a marker in between. Returns null when the engine has
 * no goal window or it lies entirely in the past.
 */
export function forecastTimelineLayout(input: {
  readonly todayMs: number;
  readonly nextCheckinAtMs: number | null;
  readonly goalMinMs: number | null;
  readonly goalMaxMs: number | null;
}): ForecastTimelineLayout | null {
  const { todayMs, nextCheckinAtMs, goalMinMs, goalMaxMs } = input;
  if (goalMinMs === null || goalMaxMs === null) return null;
  const min = Math.min(goalMinMs, goalMaxMs);
  const max = Math.max(goalMinMs, goalMaxMs);
  if (max <= todayMs) return null;
  const span = max - todayMs;
  const bandStartFraction = Math.max(0, Math.min(0.98, (min - todayMs) / span));
  const bandWidthFraction = Math.max(0.02, 1 - bandStartFraction);
  const checkinFraction =
    nextCheckinAtMs !== null && nextCheckinAtMs > todayMs && nextCheckinAtMs < max
      ? Math.max(0.04, Math.min(0.96, (nextCheckinAtMs - todayMs) / span))
      : null;
  return { checkinFraction, bandStartFraction, bandWidthFraction };
}

/** Discrete confidence segments: low=1, moderate=2, high=3, unknown/learning=0. */
export function confidenceSegments(level: string): number {
  switch (level) {
    case "low":
      return 1;
    case "moderate":
      return 2;
    case "high":
      return 3;
    default:
      return 0;
  }
}

/** Data-quality bar score (1–3); null while the engine is still learning. */
export function dataQualityScore(quality: string): number | null {
  switch (quality) {
    case "low":
      return 1;
    case "moderate":
      return 2;
    case "high":
      return 3;
    default:
      return null;
  }
}

/**
 * Most recent applied weekly adjustment (check-ins arrive newest-first;
 * pending proposals and paused rows do not count). Null when the latest
 * review changed nothing — the targets card then shows no chip.
 */
export function latestAdjustmentKcal(checkins: readonly CheckinView[]): number | null {
  const resolved = checkins.find((row) => row.status !== "pending" && !row.paused);
  if (resolved === undefined || resolved.adjustmentKcal === 0) return null;
  return resolved.adjustmentKcal;
}

/** Programme update payload for the edit sheets (subset of updateProgramme args). */
export interface ProgrammeUpdate {
  readonly goalType?: string;
  readonly targetWeightKg?: number;
  readonly weightLossPace?: string;
  readonly activityLevel?: string;
  readonly avgDailySteps?: number;
  readonly exerciseType?: string;
}

export type ProgrammeSaveResult =
  | { readonly type: "saved" }
  | { readonly type: "failed"; readonly message: string };

/** Drives api.engine.updateProgramme; the returned snapshot refreshes via the query subscription. */
export async function saveProgramme(
  update: (patch: ProgrammeUpdate) => Promise<unknown>,
  patch: ProgrammeUpdate,
): Promise<ProgrammeSaveResult> {
  try {
    await update(patch);
    console.log("[Frontend][Plan] programme updated", { fields: Object.keys(patch) });
    return { type: "saved" };
  } catch (error) {
    console.warn("[Frontend][Plan] updateProgramme failed", error);
    return { type: "failed", message: errorMessage(error) };
  }
}
